import { useInternalState, assignMswData, serializeQuery } from '../hooks/internalState';
import {
  DeleteVideoPrivateCommentReplyReactionResult,
  DeleteVideoPrivateCommentReplyReactionOptions,
  DeleteVideoPrivateCommentReplyReactionFields,
  deleteVideoPrivateCommentReplyReaction,
} from '../fetchers/video/private_comment/reply/reaction';
import { useCallback } from 'react';
import { useSWRConfig } from 'swr';
import { useGctlConfig } from '../hooks/GctlProvider';

/**
 * DO NOT MODIFY
 * Auto-generated by gctl-plugin-vimeo-hooks
 */

type UseDeleteVideoPrivateCommentReplyReactionOptions<
  Fields extends DeleteVideoPrivateCommentReplyReactionFields,
> = DeleteVideoPrivateCommentReplyReactionOptions<Fields>;

/**
 * Delete a clip comment reaction
 * This method deletes the specified clip comment reaction. The authenticated user or guest user must be the owner of the reaction.
 */

export function useDeleteVideoPrivateCommentReplyReaction<
  Fields extends DeleteVideoPrivateCommentReplyReactionFields,
>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<
    DeleteVideoPrivateCommentReplyReactionResult<Fields> | undefined
  >();

  let call = useCallback(
    async (options: UseDeleteVideoPrivateCommentReplyReactionOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/videos/${options.where.videoId}/private_comments/${options.where.commentId}/replies/${
            options.where.replyId
          }/reactions/${options.where.reactionId}${serializeQuery(options)}`,
          deleteVideoPrivateCommentReplyReaction({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
          false,
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useDeleteVideoPrivateCommentReplyReaction, {
    endpoint: '/videos/:videoId/private_comments/:commentId/replies/:replyId/reactions/:reactionId',
    method: 'DELETE',
  });
}
